"use client";

/**
 * Integration Icons
 *
 * Loads brand logos from Supabase storage CDN
 * with fallback to a colored letter circle.
 */

import { useState, useEffect } from "react";
import {
  getIntegrationColor,
  displayIntegrationSlug,
} from "@/lib/composio-tools";
import { cn } from "@/lib/utils";
import { INTEGRATION_LOGOS_CDN as STORAGE_BASE } from "@/lib/cdn";

/** Local overrides for integrations with non-SVG logos, and for the Studio
 *  (the `fal` relay is first-party: it wears our mark, never the vendor's). */
const LOCAL_ICONS: Record<string, string> = {
  replicate: "/replicate.png",
  higgsfield: "/brand/higgsfield.jpg",
  fal: "/vestra.svg",
};

interface IntegrationIconProps {
  integration: string;
  size?: number;
  className?: string;
}

/** The curated bucket's art for a slug — AppIcon's first ladder rung reuses it.
 *  Bucket art is stored under the BRAND slug (granola.svg), so `_mcp` transport
 *  suffixes are stripped before the path is built. */
export function integrationBucketSrc(slug: string): string {
  const displaySlug = displayIntegrationSlug(slug);
  return LOCAL_ICONS[displaySlug] || `${STORAGE_BASE}/${displaySlug}.svg`;
}

export function LetterFallback({
  slug,
  size,
  color,
  radius,
  className,
}: {
  slug: string;
  size: number;
  color: string;
  radius: string;
  className: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center flex-shrink-0",
        radius,
        className,
        "font-bold",
        "text-[white] leading-none"
      )}
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        fontSize: size * 0.45,
      }}
    >
      {slug.charAt(0).toUpperCase()}
    </span>
  );
}

export function IntegrationIcon({
  integration,
  size = 20,
  className = "",
}: IntegrationIconProps) {
  const slug = displayIntegrationSlug(integration);
  const color = getIntegrationColor(slug);
  const [failed, setFailed] = useState(false);

  // Reset failed state when integration prop changes (e.g. streaming: 'api' → 'gmail')
  useEffect(() => {
    setFailed(false);
  }, [slug]);

  if (failed) {
    return (
      <LetterFallback
        slug={slug}
        size={size}
        color={color}
        radius="rounded-8"
        className={className}
      />
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-8 flex-shrink-0 overflow-hidden bg-white",
        className
      )}
      style={{ width: size, height: size }}
    >
      <img
        src={LOCAL_ICONS[slug] || `${STORAGE_BASE}/${slug}.svg`}
        alt={slug}
        width={size}
        height={size}
        loading="lazy"
        className="object-contain"
        style={{ width: size * 0.85, height: size * 0.85 }}
        onError={() => setFailed(true)}
      />
    </span>
  );
}

export function IntegrationIconLarge({
  integration,
  size = 48,
  className = "",
}: IntegrationIconProps) {
  const slug = displayIntegrationSlug(integration);
  const color = getIntegrationColor(slug);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [slug]);

  if (failed) {
    return (
      <LetterFallback
        slug={slug}
        size={size}
        color={color}
        radius="rounded-16"
        className={className}
      />
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-16 flex-shrink-0 overflow-hidden bg-white",
        className
      )}
      style={{ width: size, height: size }}
    >
      <img
        src={LOCAL_ICONS[slug] || `${STORAGE_BASE}/${slug}.svg`}
        alt={slug}
        width={size}
        height={size}
        loading="lazy"
        className="object-contain"
        style={{ width: size * 0.8, height: size * 0.8 }}
        onError={() => setFailed(true)}
      />
    </span>
  );
}

export const SUPPORTED_INTEGRATIONS = [
  "gmail",
  "github",
  "slack",
  "notion",
  "googledrive",
  "googlecalendar",
  "googlesheets",
  "trello",
  "jira",
  "linear",
  "discord",
  "twitter",
  "stripe",
  "hubspot",
  "salesforce",
  "figma",
  "zoom",
];
